import { DOMUtils } from '../../utils/dom'
import { PageType } from '../common/page-types'
import { IProviderConfig } from '../common/provider-config'
import { Ad, AdType, IAd } from './ad'
import {
    IAdBiddingAdapter,
    IAdBiddingProviderConfig,
    IAdServiceAdapter,
    IAdVerifierAdapter
} from './adapters/adapter'
import { AdURIFormatter } from './formatters/uri'
import { IAdTargetStrategyHandler } from './targeting/strategy'

export interface IAdServiceConfig {
    // provider configs
    provider: IProviderConfig
    bidding: IAdBiddingProviderConfig[]
    verifier: IProviderConfig

    // subscription-related (for ux)
    adLight?: boolean

    // page meta (for targeting)
    ancestry?: string[]
    articleId?: string
    authors?: string[]
    network: string
    originId?: string
    page?: PageType
    property: string
    siteId: string
}

export interface IAdService {
    initialize(conf: IAdServiceConfig): void
    update(isSubscriber: boolean): void
}

export class AdService implements IAdService {
    /**
     * Required DOM selectors and attributes.
     *
     * IMPORTANT: These will be duplicated elsewhere (templates, css) so ensure changes are coordinated
     * and instances minimized.
     */
    public static DOM = {
        attrs: {
            adLightContent: 'data-ad-lite',
            fallback: 'data-ad-fallback',
            loc: 'data-ad-loc', // optional; when multiple of same size on a page; e.g. top, mid, bot
            oop: 'data-ad-oop', // for out-of-page slots (intersitials); no size attribute value required for these
            size: 'data-ad', // main selector; size array optional (default is bigbox) but encouraged
            sticky: 'data-ad-sticky', // main selector for stickyness; size array optional for which behaviour
                                      // applies(default is all)
            stickySec: 'data-ad-sticky-sec', // number of seconds for sticky behaviour
            target: 'data-ad-target', // JSON object of <string>key:<string|string[]>values for ad-level targeting
                                      // NOTE: rarely used
            type: 'data-ad-type' // TODO: not currently supported, but pretty sure it'll be used (e.g. video?)
        }
    }
    public registered = false
    protected ad: typeof Ad
    protected adLite = false

    /**
     * Stores adapter, formatter, targeter refs.
     */
    public constructor(
        private adapter: IAdServiceAdapter & IAdBiddingAdapter & IAdVerifierAdapter,
        private formatter: AdURIFormatter,
        private targeting: IAdTargetStrategyHandler) { }

    /**
     * Registers ads in DOM via primary attribute selector (slot), configure targeting.
     */
    public initialize(conf: IAdServiceConfig): void {

        this.adapter.adURI = this.formatter(conf.provider.account, conf.siteId, conf.ancestry, conf.page)
        this.adapter.initializeBidding(conf.bidding)
        this.adapter.initializeVerifier(conf.verifier)

        // configure page level targeting (applies to all ads on a page)
        this.adapter.target(
            this.targeting.page({
                ancestry: conf.ancestry,
                articleId: conf.articleId,
                authors: conf.authors,
                network: conf.network,
                originId: conf.originId,
                property: conf.property,
                type: conf.page}))

        this.update(conf.adLight)
    }

    public update(adLight: boolean) {
        // haven't yet registered any ads
        if (!this.registered) {
            this.registered = this.registered || !adLight
            this.registerAds(adLight).catch((e) => console.log(e))

        // already registered ads, now re-register for ad-light experience
        } else if (adLight) {
            this.adLite = true
            this.registerAds(adLight).catch((e) => console.log(e))

        // already registered as ad-light, so re-register for normal experience
        } else if (this.adLite) {
            this.adLite = false
            this.registerAds(adLight).catch((e) => console.log(e))
        }
    }

    /**
     * Registers ads with service adapter or displays subscriber messaging.
     */
    private async registerAds(adLight: boolean) {
        await DOMUtils.ready

        // has sticky ad(s), load mixin
        if (!this.ad && document.querySelector(`[${AdService.DOM.attrs.sticky}]`)) {
            const { WithSticky } = await import('./ad/sticky')
            this.ad = WithSticky(Ad)
        } else {
            this.ad = Ad
        }

        this.getAds().forEach((ad) => {
            if (adLight) {
                if (this.registered) {
                    this.adapter.destroy([ad])
                }
                // currently collapsing ads for light experience
                ad.fallBack('')
            } else {
                this.adapter.registerAd(ad)
            }
        })

        // request
        if (!(this.registered && adLight)) {
            this.adapter.requestAds()
        }
    }

    /**
     * Retrieve all ads from DOM.
     */
    private getAds(): IAd[] {
        return [...document.querySelectorAll(`[${AdService.DOM.attrs.size}]`)]
                .map(this.toAd.bind(this))
    }

    /**
     * Initializes `Ad` from DOM element.
     */
    private toAd(el: Element): Ad {
        return new this.ad(
            el,
            JSON.parse(el.getAttribute(AdService.DOM.attrs.size) || 'null') || undefined,
            this.targeting.ad(el),
            el.hasAttribute(AdService.DOM.attrs.oop),
            el.getAttribute(AdService.DOM.attrs.type) as AdType)
    }
}
